// src/components/Layout.js

import React, { useEffect, useState } from 'react';
import axiosInstance from '../utils/axiosInstance';

import { styled, useTheme } from '@mui/material/styles';
import { Box, CssBaseline } from '@mui/material';
import { Outlet } from 'react-router-dom';
import Sidebar from './Sidebar';
import Footer from './Footer';
import Header from './Header';
import { useNavigate } from 'react-router-dom';
import useMediaQuery from '@mui/material/useMediaQuery';
import backendURL, { google_login_api, SETUP_STEP_LOCAL_KEY, SETUP_COMPLETE_LOCAL_KEY } from "../config";
import { useChatbots } from '../contexts/ChatbotContext';

const API_BASE_URL = backendURL;

const drawerWidth = 300;

const LayoutRoot = styled(Box)(({ theme }) => ({
    display: 'flex',
    minHeight: '100vh',
    backgroundColor: theme.palette.mode === 'dark' 
        ? '#131b2b'
        : '#f1f5f9',
}));

const MainAreaContainer = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'isCollapsed' && prop !== 'isMobile'
})(({ theme, isCollapsed, isMobile }) => ({
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    marginLeft: !isCollapsed && !isMobile ? `${drawerWidth}px` : 0,
    transition: theme.transitions.create('margin', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    ...( !isMobile && !isCollapsed && {
        transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.enteringScreen,
        }),
    }),
    minHeight: '100vh',
    backgroundColor: theme.palette.background.default,
}));


const ContentWrapper = styled(Box)(({ theme }) => ({
    flexGrow: 1,
    padding: theme.spacing(3),
    backgroundColor: theme.palette.mode === 'dark' 
    ? '#131b2b'
    : '#f1f5f9',
}));

const FooterWrapper = styled(Box)(({ theme }) => ({
    padding: theme.spacing(2),
    backgroundColor: theme.palette.mode === 'dark' 
    ? '#131b2b'
    : '#f1f5f9',
}));

function Layout({ isCollapsed, onToggleSidebar }) {
    const navigate = useNavigate();
    const theme = useTheme();
    const [userData, setUserData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isMobileView, setIsMobileView] = useState(false);
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    useEffect(() => {
        const userStr = localStorage.getItem('user');
        if (userStr) {
            try {
                const parsedUser = JSON.parse(userStr);
                setUserData(parsedUser);
            } catch (error) {
                console.error('Error parsing user data:', error);
                localStorage.removeItem('user');
                navigate('/Login');
            }
        } else {
            navigate('/Login');
        }
        setIsLoading(false);
    }, [navigate]);

    useEffect(() => {
        if (!userData || isLoading) return;
        console.log("Checking auth and subscription");
        
        const checkAuthAndSubscription = async () => {
            try {
                const subPromise = axiosInstance.get(`/subscriptions/current`);
                const [subResult] = await Promise.allSettled([subPromise]);
                let fetchedSubscription = null;

                if (subResult.status === 'fulfilled' && subResult.value?.data) {
                    fetchedSubscription = subResult.value.data.data || subResult.value.data;
                    if (!fetchedSubscription || !fetchedSubscription.plan) {
                        if (fetchedSubscription?.status !== 'freemium') {
                            fetchedSubscription = null;
                        }
                    }
                } else if (subResult.reason?.response?.status === 404) {
                    const responseData = subResult.reason.response.data;
                    fetchedSubscription = responseData?.data?.plan?.name === 'freemium' ? responseData.data : null;
                } else {
                    console.error("Subscription fetch failed:", subResult.reason);
                }
                
                const userStr = localStorage.getItem('user');
                if (userStr) {
                    const prevUser = JSON.parse(userStr);
                    const updatedUser = {
                        ...prevUser,
                        active_team: {
                            ...prevUser.active_team,
                            subscription: fetchedSubscription,
                        },
                        teams: prevUser.teams.map(team =>
                            team.team_id === prevUser.active_team.team_id
                                ? { ...team, subscription: fetchedSubscription }
                                : team
                        ),
                    };
                    localStorage.setItem('user', JSON.stringify(updatedUser));
                }
                console.log(fetchedSubscription);

                if (!fetchedSubscription) {
                    localStorage.removeItem(SETUP_COMPLETE_LOCAL_KEY);
                    navigate('/SignUp-Process-Steps');
                }
            } catch (err) {
                console.error("Unexpected error during subscription fetch:", err);
            }
        };
    
        checkAuthAndSubscription();
    }, [userData?.access_token, isLoading, navigate]);

    useEffect(() => {
        setIsMobileView(isMobile);
        setIsSidebarOpen(!isMobile);
    }, [isMobile]);

    const toggleSidebar = () => {
        setIsSidebarOpen(!isSidebarOpen);
    };

    const handleMobileSidebarClose = () => {
        if (isMobileView) {
            setIsSidebarOpen(false);
        }
    };

    if (!userData) {
        return null;
    }

    return (
        <LayoutRoot>
            <CssBaseline />
            <Header toggleSidebar={toggleSidebar} isSidebarCollapsed={!isSidebarOpen} isMobile={isMobileView} />
            <Sidebar
                isCollapsed={isSidebarOpen}
                isOpen={isSidebarOpen}
                drawerWidth={drawerWidth}
                onClose={handleMobileSidebarClose}
                isMobile={isMobileView}
            />
            <MainAreaContainer isCollapsed={isSidebarOpen} isMobile={isMobileView}>
                <ContentWrapper>
                    <Outlet />
                </ContentWrapper>
                <FooterWrapper>
                    <Footer />
                </FooterWrapper>
            </MainAreaContainer>
        </LayoutRoot>
    );
}

export default Layout;